const COMBINING_MARK_REGEX = /\p{M}/gu;

/**
 * Fold accents for search comparison by decomposing characters and stripping
 * combining marks ("Axé" → "axe"). Does not rewrite distinct letters such as ø.
 */
export function foldSearchText(value: string): string {
  return value.normalize('NFD').replace(COMBINING_MARK_REGEX, '').toLowerCase();
}

/**
 * Same fold as foldSearchText, plus indexMap[foldedOffset] → original offset.
 * Offsets are UTF-16 code units so they line up with indexOf / .length.
 * Combining marks are omitted from the folded string so a full match can still
 * map back across the original accented character.
 *
 * The folded string is produced from the whole input so context-sensitive
 * casing (Greek final sigma) matches foldSearchText. The index map is built
 * from successive prefix folds so each added folded unit still points at the
 * original character that produced it.
 */
export function foldSearchTextWithMap(value: string): {
  folded: string;
  indexMap: number[];
} {
  const folded = foldSearchText(value);
  const indexMap: number[] = [];
  let originalIndex = 0;
  let previousFoldedLength = 0;

  for (const char of value) {
    const nextFolded = foldSearchText(
      value.slice(0, originalIndex + char.length),
    );
    const addedLength = Math.max(0, nextFolded.length - previousFoldedLength);

    for (let index = 0; index < addedLength; index++) {
      indexMap.push(originalIndex);
    }

    previousFoldedLength = nextFolded.length;
    originalIndex += char.length;
  }

  return { folded, indexMap };
}

/**
 * Convert a folded exclusive index to the matching exclusive offset in the original string.
 */
export function originalOffsetForFoldedIndex(
  indexMap: number[],
  originalLength: number,
  foldedIndex: number,
): number {
  if (foldedIndex <= 0) {
    return 0;
  }

  if (foldedIndex >= indexMap.length) {
    return originalLength;
  }

  return indexMap[foldedIndex];
}
